import { useState } from 'react';
import { Link } from 'react-router-dom';
import { DecisionLevelSlideout } from '../../custom-components/DecisionLevelSlideout';
import { FunctionalGroupSlideout } from '../../custom-components/FunctionalGroupSlideout';
import { RankExplanationSlideout } from '../../custom-components/RankExplanationSlideout';
import { RoleTag } from './RoleTag';
import { EvidenceList } from './EvidenceList';
import { RoleTimeline } from './RoleTimeline';
import { TitleFragment } from './TitleFragment';
import './PersonDetailRole.scss';

interface Evidence {
  id?: string;
  observedAt?: string;
  sourceType?: string;
  firstName?: string;
  middleName?: string;
  lastName?: string;
  normalizedTitle?: string;
  url?: {
    screenshotKey?: string | null;
  };
}

interface Role {
  role?: string;
  area?: string;
  modifier?: string;
  status?: string;
  title?: string;
  rank?: number | null;
  firstObservedAt?: string;
  lastObservedAt?: string;
  evidenceCount?: number;
  evidence?: Evidence[];
  decisionLevel?: string | null;
  functionalGroup?: string | null;
  institution?: {
    id: string;
    name?: string;
    type?: string;
    loc_state?: string;
  };
}

interface PersonDetailRoleProps {
  role: Role;
}

function getRankTier(rank: number): number {
  if (rank < 25) return 0;
  if (rank < 50) return 1;
  return 2;
}

export function PersonDetailRole({ role }: PersonDetailRoleProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isRankSlideoutOpen, setIsRankSlideoutOpen] = useState(false);
  const [isDecisionLevelSlideoutOpen, setIsDecisionLevelSlideoutOpen] = useState(false);
  const [isFunctionalGroupSlideoutOpen, setIsFunctionalGroupSlideoutOpen] = useState(false);

  return (
    <div
      className="person-detail-role"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="person-detail-role__header">
        <div className="person-detail-role__header-left">
          {(role.decisionLevel || role.functionalGroup) && (
            <div className="person-detail-role__fragments">
              {role.decisionLevel && <RoleTag type="decisionLevel" value={role.decisionLevel} onClick={() => setIsDecisionLevelSlideoutOpen(true)} />}
              {role.functionalGroup && <RoleTag type="functionalGroup" value={role.functionalGroup} onClick={() => setIsFunctionalGroupSlideoutOpen(true)} />}
            </div>
          )}
          {(role.modifier || role.role || role.area || role.status) && (
            <div className="person-detail-role__fragments person-detail-role__fragments--secondary">
              {role.modifier && <TitleFragment label="Modifier" value={role.modifier} colorMode="grayscale" />}
              {role.role && <TitleFragment label="Role" value={role.role} colorMode="grayscale" />}
              {role.area && <TitleFragment label="Area" value={role.area} colorMode="grayscale" />}
              {role.status && <TitleFragment label="Status" value={role.status} colorMode="grayscale" />}
            </div>
          )}
        </div>
        {role.rank != null && (
          <span
            className={`person-detail-role__rank person-detail-role__rank--tier-${getRankTier(role.rank)} person-detail-role__rank--clickable`}
            style={{ '--rank-percent': `${role.rank}%` } as React.CSSProperties}
            onClick={() => setIsRankSlideoutOpen(true)}
          >
            <span className="person-detail-role__rank-label">Rank:</span> {role.rank}
          </span>
        )}
      </div>
      <div className="person-detail-role__timeline">
        <RoleTimeline
          firstSeen={role.firstObservedAt}
          lastSeen={role.lastObservedAt}
          sightings={role.evidenceCount}
          evidenceItems={role.evidence?.filter((e) => e.observedAt).map((e) => ({
            date: e.observedAt!,
            sourceType: e.sourceType,
            firstName: e.firstName,
            middleName: e.middleName,
            lastName: e.lastName,
          }))}
        />
      </div>
      {role.institution && (
        <div className="person-detail-role__institution">
          <Link to={`/directory/institutions/${role.institution.id}`}>
            {role.institution.name}
          </Link>
        </div>
      )}
      {isExpanded && role.evidence && (
        <>
          <div
            className="person-detail-role__expand-container person-detail-role__expand-container--top"
            onClick={() => setIsExpanded(false)}
          >
            <span className="person-detail-role__expand-text">Collapse</span>
          </div>
          <div className="person-detail-role__evidence-title">
            Observations contributing to this role
          </div>
          <div className="person-detail-role__evidence-list">
            <EvidenceList evidence={role.evidence} />
          </div>
        </>
      )}
      <div
        className={`person-detail-role__expand-container ${isExpanded ? 'person-detail-role__expand-container--bottom' : ''}`}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        {(isHovered || isExpanded) && (
          <span className="person-detail-role__expand-text">
            {isExpanded ? 'Collapse' : 'Expand'}
          </span>
        )}
      </div>

      <RankExplanationSlideout
        isOpen={isRankSlideoutOpen}
        onClose={() => setIsRankSlideoutOpen(false)}
      />
      <DecisionLevelSlideout
        isOpen={isDecisionLevelSlideoutOpen}
        onClose={() => setIsDecisionLevelSlideoutOpen(false)}
      />
      <FunctionalGroupSlideout
        isOpen={isFunctionalGroupSlideoutOpen}
        onClose={() => setIsFunctionalGroupSlideoutOpen(false)}
      />
    </div>
  );
}
